const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const GEO_API = `${API_BASE}/GeoLocation/GetGeoLocation`;
const LEAD_API = `${API_BASE}/api/Lead/AddLead`;

// Country codes list with dial codes
export const countryCodes = [
  { dialCode: 1, country: 'US', label: 'US +1' },
  { dialCode: 1, country: 'CA', label: 'CA +1' },
  { dialCode: 44, country: 'GB', label: 'UK +44' },
  { dialCode: 49, country: 'DE', label: 'DE +49' },
  { dialCode: 33, country: 'FR', label: 'FR +33' },
  { dialCode: 61, country: 'AU', label: 'AU +61' },
  { dialCode: 91, country: 'IN', label: 'IN +91' },
  { dialCode: 86, country: 'CN', label: 'CN +86' },
  { dialCode: 81, country: 'JP', label: 'JP +81' },
  { dialCode: 82, country: 'KR', label: 'KR +82' },
  { dialCode: 55, country: 'BR', label: 'BR +55' },
  { dialCode: 52, country: 'MX', label: 'MX +52' },
  { dialCode: 971, country: 'AE', label: 'AE +971' },
  { dialCode: 966, country: 'SA', label: 'SA +966' },
  { dialCode: 65, country: 'SG', label: 'SG +65' },
  { dialCode: 852, country: 'HK', label: 'HK +852' },
  { dialCode: 31, country: 'NL', label: 'NL +31' },
  { dialCode: 34, country: 'ES', label: 'ES +34' },
  { dialCode: 39, country: 'IT', label: 'IT +39' },
  { dialCode: 41, country: 'CH', label: 'CH +41' },
  { dialCode: 46, country: 'SE', label: 'SE +46' },
  { dialCode: 47, country: 'NO', label: 'NO +47' },
  { dialCode: 45, country: 'DK', label: 'DK +45' },
  { dialCode: 358, country: 'FI', label: 'FI +358' },
  { dialCode: 48, country: 'PL', label: 'PL +48' },
  { dialCode: 43, country: 'AT', label: 'AT +43' },
  { dialCode: 32, country: 'BE', label: 'BE +32' },
  { dialCode: 353, country: 'IE', label: 'IE +353' },
  { dialCode: 351, country: 'PT', label: 'PT +351' },
  { dialCode: 64, country: 'NZ', label: 'NZ +64' },
  { dialCode: 27, country: 'ZA', label: 'ZA +27' },
  { dialCode: 234, country: 'NG', label: 'NG +234' },
  { dialCode: 254, country: 'KE', label: 'KE +254' },
  { dialCode: 20, country: 'EG', label: 'EG +20' },
  { dialCode: 60, country: 'MY', label: 'MY +60' },
  { dialCode: 63, country: 'PH', label: 'PH +63' },
  { dialCode: 66, country: 'TH', label: 'TH +66' },
  { dialCode: 62, country: 'ID', label: 'ID +62' },
  { dialCode: 84, country: 'VN', label: 'VN +84' },
  { dialCode: 90, country: 'TR', label: 'TR +90' },
  { dialCode: 7, country: 'RU', label: 'RU +7' },
  { dialCode: 380, country: 'UA', label: 'UA +380' },
  { dialCode: 972, country: 'IL', label: 'IL +972' },
  { dialCode: 92, country: 'PK', label: 'PK +92' },
  { dialCode: 880, country: 'BD', label: 'BD +880' },
  { dialCode: 94, country: 'LK', label: 'LK +94' },
  { dialCode: 56, country: 'CL', label: 'CL +56' },
  { dialCode: 57, country: 'CO', label: 'CO +57' },
  { dialCode: 54, country: 'AR', label: 'AR +54' },
  { dialCode: 51, country: 'PE', label: 'PE +51' },
];

// Cached geo data
let geoCache = null;

/**
 * Fetch geolocation data for the current user.
 * Caches the result so it only calls the API once per session.
 */
export async function getGeoLocation() {
  if (geoCache) return geoCache;

  try {
    const res = await fetch(GEO_API);
    if (!res.ok) {
      console.warn('GeoLocation API returned status:', res.status);
      return null;
    }
    const data = await res.json();
    console.log('GeoLocation API response:', data);
    if (data.isSuccess) {
      geoCache = data;
      return data;
    }
  } catch (err) {
    console.warn('GeoLocation API error:', err);
  }
  return null;
}

/**
 * Find the matching country entry from countryCodes list by country code string (e.g. "US", "PK").
 */
export function findCountryByCode(code) {
  if (!code) return countryCodes[0]; // default US
  const upper = code.toUpperCase();
  return countryCodes.find(c => c.country === upper) || countryCodes[0];
}

/**
 * Submit a lead to the CRM API.
 * Returns { success, data, error } based on response status.
 */
export async function submitLead({ name, email, countryDialCode, country, phoneNumber, description, city, state, ipAddress, geoLocation }) {
  const payload = {
    name,
    email,
    countryDialCode: Number(countryDialCode),
    country: country || '',
    phoneNumber: phoneNumber.replace(/\D/g, ''), // numeric only
    ipAddress: ipAddress || '',
    description: description || '',
    pageUrl: window.location.href,
    city: city || '',
    state: state || '',
    geoLocation: geoLocation || '',
    packageTitle: '',
    packageAmount: 0
  };

  console.log('Submitting lead payload:', payload);

  try {
    const res = await fetch(LEAD_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    let data;
    const responseText = await res.text();
    console.log('Lead API raw response:', responseText);
    
    try {
      data = JSON.parse(responseText);
    } catch {
      console.error('Failed to parse response as JSON:', responseText);
      return { success: false, error: `Server error (${res.status}): ${responseText || 'No response body'}` };
    }
    
    console.log('Lead API response:', data);

    // Check for success (API returns 200 with isSuccess true and customerId)
    if (res.status === 200 && data.isSuccess && data.data?.customerId) {
      return { success: true, customerId: data.data.customerId };
    }

    // Handle API returning 200 but with error in body (statusCode 401)
    if (data.statusCode === 401) {
      return { success: false, error: data.message || 'Unauthorized request' };
    }

    // Handle HTTP 401
    if (res.status === 401) {
      return { success: false, error: data.message || 'Unauthorized request' };
    }

    // Handle HTTP 400
    if (res.status === 400) {
      // Check for validation errors object
      if (data.errors) {
        const errorMessages = Object.values(data.errors).flat().join(', ');
        return { success: false, error: errorMessages || 'Validation failed' };
      }
      return { success: false, error: data.errorMessage || data.message || data.title || 'Invalid request' };
    }

    return { success: false, error: data.errorMessage || data.message || 'Something went wrong' };
  } catch (err) {
    console.error('Lead API error:', err);
    return { success: false, error: 'Network error. Please try again.' };
  }
}
